import { onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import {
  useAuthStore,
  useUserStore,
  useAccountStore,
  useLeverageStore,
  useCountryStore,
  useExternalServerStore,
  useSvgStore,
} from '@/store'
import { useVerifyStore } from '@/store/verify'
import { useInviteStore } from '@/store/invite'
import { STORAGE_KEYS } from '@/constants'
import storage from 'store'
import { http } from '@/libs'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { useAdvertisingStore } from '@/store/advertising'
import { useI18n } from 'vue-i18n'

const { DEVICE_ID } = STORAGE_KEYS
// ===== 模块级 once 标记（运行期内有效） =====
let __BOOT_PUBLIC_ONCE__ = false
let __BOOT_PRIVATE_ONCE__ = false

// 并发去重：用 Promise 作为“单飞锁”
let _publicInitPromise: Promise<void> | null = null
let _privateInitPromise: Promise<void> | null = null

export function useAppBootstrap() {
  const router = useRouter()
  const { locale } = useI18n()
  const auth = useAuthStore()

  const user = useUserStore()
  const account = useAccountStore()
  const leverage = useLeverageStore()
  const country = useCountryStore()
  const externalServer = useExternalServerStore()
  const verifyStore = useVerifyStore()
  const inviteStore = useInviteStore()
  const svgStore = useSvgStore()
  const advertisingStore = useAdvertisingStore()

  // —— 是否“受保护路由”：未显式标记 public=true 的都视为需要鉴权（与路由守卫一致）
  const isProtectedRoute = computed(
    () => !router.currentRoute.value.matched.some((r) => r.meta?.public === true),
  )

  const getDeviceId = async () => {
    try {
      const fingerPrint = await FingerprintJS.load()
      const { visitorId } = await fingerPrint.get()
      const data = await http.post<{ device_id: string }>(
        '/api/aff_device/add',
        { unique_id: visitorId, client_type: '3' },
        { useAuth: false },
      )
      if (data) storage.set(DEVICE_ID, data.data?.device_id)
    } catch (error) {
      throw error
    }
  }
  // ========== 公共初始化（一次性，无需登录） ==========
  const bootstrapPublic = async () => {
    if (__BOOT_PUBLIC_ONCE__) return
    if (_publicInitPromise) return _publicInitPromise

    _publicInitPromise = (async () => {
      // 等待语言设置完成，确保请求头正确
      await new Promise((resolve) => setTimeout(resolve, 0))

      await Promise.all([
        country.init().catch(() => {}),
        leverage.init().catch(() => {}),
        getDeviceId().catch((error) => {
          console.error('getDeviceId', error)
        }),
        // … 其它无需 token 的公共数据
      ])
      __BOOT_PUBLIC_ONCE__ = true
    })().finally(() => {
      _publicInitPromise = null
    })

    return _publicInitPromise
  }

  // ========== 私有初始化（一次性：仅当“已登录 && 当前路由受保护”时触发） ==========
  const bootstrapPrivate = async () => {
    if (__BOOT_PRIVATE_ONCE__) return
    if (_privateInitPromise) return _privateInitPromise
    if (!auth.isAuthenticated || !isProtectedRoute.value) return

    _privateInitPromise = (async () => {
      await Promise.all([
        user.fetchUserInfo().catch(() => {}),
        account.init({ withCategory: true, withExternal: true }).catch(() => {}),
        leverage.init().catch(() => {}),
        externalServer.init().catch(() => {}),
        verifyStore.initVerifyInfo().catch(() => {}),
        inviteStore.getInviteGroupOptions().catch(() => {}),
        svgStore.init({ image_type: 'svg' }).catch(() => {}),
        advertisingStore.getAdvertising().catch(() => {}),
        // ⚠️ 公共初始化里已做的，不必重复
      ])
      __BOOT_PRIVATE_ONCE__ = true
    })().finally(() => {
      _privateInitPromise = null
    })

    return _privateInitPromise
  }

  // 进入应用即尝试公共初始化；若初次进入离线，联网后补跑一次
  onMounted(() => {
    bootstrapPublic()
    const onceHandler = () => bootstrapPublic()
    window.addEventListener('online', onceHandler, { once: true })
  })

  watch(
    () => auth.isAuthenticated,
    async (ok) => {
      if (!ok || __BOOT_PRIVATE_ONCE__) return
      await bootstrapPrivate()
    },
    { immediate: true },
  )

  // 登出后复位，便于下一个用户重新跑私有初始化
  watch(
    () => auth.isAuthenticated,
    (loggedIn, wasLoggedIn) => {
      if (!loggedIn && wasLoggedIn) {
        __BOOT_PRIVATE_ONCE__ = false
        _privateInitPromise = null
      }
    },
  )
}
